<i18n>
{
	"en": {
		"editTemplate": "Edit deviation type",
		"newDeviationType": "Add deviation type",
		"deleteTerminal": "Remove deviation type | Remove deviation type | Remove deviation types",
		"noDeviationTypesFound": "No Deviation Types...",
		"header": "Deviation Types",
		"code": "Deviation Code",
		"description": "Description",
		"size": "Size",
		"editedDate": "Edited Date",
		"failedToFetch": "Failed to get deviation-type data",
		"failedToDelete": "Failed to delete deviation type",
		"deletedDeviationType": "Deleted deviation type | Deleted deviation type | Deleted {count} deviation types"
	},
	"no": {
		"editTemplate": "Rediger avvikstype",
		"newDeviationType": "Legg til avvikstype",
		"deleteTerminal": "Fjern avvikstype | Fjern avvikstype | Fjern avvikstyper",
		"noDeviationTypesFound": "Ingen avvikstyper...",
		"header": "Avvikstyper",
		"code": "Avvik, kode",
		"description": "Beskrivelse",
		"size": "Størrelse",
		"editedDate": "Endret dato",
		"failedToFetch": "Kunne ikke hente avvikstype data",
		"failedToDelete": "Kunne ikke slette avvikstype",
		"deletedDeviationType": "Avvikstypen ble slettet | Avvikstypen ble slettet | Slettet {count} avvikstyper"
	}
}
</i18n>
<template>
	<t-vertical-panel class="deviation-list">
		<div slot="header" class="deviation-list-header">
			<t-panel-header size="md" type="bp" icon="random" >
				{{ $tc('header', deviationTypesFound) }}
			</t-panel-header>
		</div>
		<div slot="sub-header" class="deviation-list-sub-header">
			<t-button
				:disabled="true"
				iconBefore="annotation"
				appearance="minimal"
				@click="editTemplate()"
			>
				<!-- :disabled="rowsSelected !== 1" -->
				{{ $t('editTemplate') }}</t-button>
			<t-button
				:disabled="true"
				iconBefore="trash"
				appearance="minimal"
				width="150px"
				type="bp"
				@click="onDeleteDeviationType"
			>
				<!-- :disabled="rowsSelected == 0" -->
				{{$tc('deleteTerminal', rowsSelected)}}</t-button>
			<t-button
				:disabled="true"
				appearance="primary"
				width="150px"
				@click="onAddDeviationType"
			>{{$t('newDeviationType')}}</t-button>
		</div>
		<div class="deviation-list-content">
			<t-grid
				v-if="deviationTypesFound"
				ref="grid"
				:columnDefinitions="[
					{
						field: 'code', displayName: $t('code'),
						width: 200,
					},{
						field: 'description', displayName: $t('description')
					},{
						field: 'editedDate.date', displayName: $t('editedDate'),
						valueGetter: item => dateFormatter(item.editedDate.date),
						width: 200, resizable: false,
						type: 'date',
						comparableValueGetter: item => moment(item.editedDate.date, storageDateFormat).toISOString()
					}
				]"
				:defaultColumnDefinition="defaultColumnDefinition"
				:rowHeight="40"
				:items="deviationTypes"
				:selectableMode="['checkbox']"
				localStorageId="deviationTypesSearchList"
				selectable
				@selected="onRowsSelected"
				@rowDoubleClick="editTemplate($event)"
				@enter="editTemplate()"
			/>
			<t-no-content-panel v-else :text="$t('noDeviationTypesFound')" icon="order" />
			<t-overlay
				:isShown="loading"
				absolute
				loader
			/>
		</div>
		<EditDeviationType/>
	</t-vertical-panel>
</template>

<script>
import { mapState, mapActions } from './store/deviations.store.js';
import { mapState as settingsState } from '@/modules/settings/store/settings.store.js';
import Grid from '@/components/evergreen/timpex/grid/TGrid';
import { storageDateFormat, validate } from '@/core';
import moment from 'moment';
import EditDeviationType from './EditDeviationType';
import deviationsSchema from './Deviations.schema.json';

export default {
	name: 'DeviationTypes',
	components: { TGrid: Grid, EditDeviationType },
	data() {
		return {
			defaultColumnDefinition: {
				resizable: true,
				headerFilterInputWithButton: true,
			},
			rowsSelected: 0,
		};
	},
	computed: {
		...mapState([
			'deviationTypes',
			'loading'
		]),
		...settingsState(['locale',]),
		deviationTypesFound() {
			return this.deviationTypes.length;
		},
	},
	deviationTypes: {
		immediate: true,
		deep: true,
		handler(newVal, oldVal) {
			if ( (!oldVal && newVal && newVal.length === 0) ||
					(oldVal && newVal && oldVal.length === 0 && newVal.length === 0) ) return;
			validate( deviationsSchema, { deviationTypes: this.deviationTypes });
		}
	},
	async mounted() {
		try {
			await this.getDeviationTypes();
		} catch (e) {
			this.$notify({
				type: 'error',
				title: this.$t('failedToFetch')
			});
		}
	},
	methods: {
		...mapActions([
			'getDeviationTypes',
			'setDeviationTypeTemplate',
			'setDeviationTypeEditId',
			'showEditDeviationType',
			'deleteDeviationTypes',
		]),
		dateFormatter(date) {
			if (!date) return '';
			return moment(date, storageDateFormat).format('LLL');
		},
		onRowsSelected(ids) {
			this.rowsSelected = ids.length;
		},
		editTemplate(id) {
			let deviationType = undefined;
			if (id) {
				deviationType = this.deviationTypes.find( (x) => x.id === id);
			} else {
				let selectedItems = this.$refs.grid.getSelectedItems();
				if (selectedItems.length > 1) return;
				[deviationType] = selectedItems;
			}
			if (deviationType && deviationType.id) {
				this.setDeviationTypeTemplate(deviationType);
				this.setDeviationTypeEditId(deviationType.id);
			}
			this.showEditDeviationType(true);
		},
		async onDeleteDeviationType() {
			let selectedDeviationTypes = this.$refs.grid.getSelectedItems();
			if (!selectedDeviationTypes) return;
			try {
				await this.deleteDeviationTypes(selectedDeviationTypes);
				this.$notify({
					type: 'success',
					title: this.$tc('deletedDeviationType', selectedDeviationTypes.length)
				});
				this.rowsSelected = 0;
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToDelte')
				});
			}
		},
		async onAddDeviationType() {
			this.showEditDeviationType(true);
		}
	},
};
</script>


<style lang="scss" scoped>
.deviation-list {
	.deviation-list-header {
		height: 100%;
		display: flex;
		align-items: center;
		padding-left: 15px;
	}
	.deviation-list-sub-header {
		display: flex;
		padding: 0px 15px;
		height: 100%;
		align-items: center;
		justify-content: flex-end;

		> button {
			margin: 10px;
		}
	}
	.deviation-list-content {
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 0px 5px
	}
}
</style>
